import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import create from 'zustand'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { Stack } from '@mui/system'
import { useEnterEditorPage } from '@editorHooks'
import { DownloadDocumentItem, FoundationLoadsExport } from './components'
import DownloadDocumentItemWithTask from './components/DownloadDocumentItemWithTask'
import PositionPlanDxf from './components/PositionPlanDxf'
import { tabConfig } from './constants'

dayjs.extend(customParseFormat)

/** Active export tab (formerly the `tab` query param; session-only by design). */
const useExportTabStore = create<{
  tab: keyof typeof tabConfig | undefined
  setTab: (tab: keyof typeof tabConfig) => void
}>(set => ({
  tab: undefined,
  setTab: tab => set({ tab }),
}))

const Export = () => {
  useEnterEditorPage('export')

  const tab = useExportTabStore(state => state.tab)
  const setTabStore = useExportTabStore(state => state.setTab)

  const setTab = useCallback(
    (tab: keyof typeof tabConfig) => {
      setTabStore(tab)
    },
    [setTabStore],
  )

  const drawerContent = useMemo(() => {
    return (
      <Box>
        <TabContext value={tab || tabConfig.loads.value}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <TabList onChange={(e, v) => setTab(v)}>
              {Object.entries(tabConfig).map(([value, { label }]) => (
                <Tab
                  key={value}
                  value={value}
                  label={label}
                  data-cy={`tab-${value}`}
                  sx={{ margin: 0 }}
                />
              ))}
            </TabList>
          </Box>

          <TabPanel value={tabConfig.models.value} keepMounted>
            <Box marginX={-2} mt={-2}>
              <Stack direction="column" spacing={1}>
                <DownloadDocumentItem
                  title="Modell (JSON)"
                  path="planar-model" // export/planar-model
                  params={{}}
                  type="application/json"
                  filename="PlanarModel"
                  extension="json"
                />
              </Stack>
            </Box>
          </TabPanel>
          <TabPanel value={tabConfig.loads.value} keepMounted>
            <Box marginX={-2} mt={-2}>
              <Stack direction="column" spacing={1}>
                <FoundationLoadsExport />

                <DownloadDocumentItem
                  title="Erdbebenmodell (RFEM)"
                  path="seismic-model/rfem"
                  type="application/pdf"
                  filename="seismic-model"
                  extension="json"
                />

                <Divider />
              </Stack>
            </Box>
          </TabPanel>
          <TabPanel value={tabConfig.certificates.value} keepMounted>
            <Box marginX={-2} mt={-2}>
              <Stack direction="column" spacing={1}>
                <DownloadDocumentItemWithTask
                  title="Ersatz-Schubmodule (Spektralanalyse)"
                  apiDocumentName="shear-modulus"
                  params={{
                    extension: 'docx',
                  }}
                  type="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  filename="Ersatz-Schubmodule"
                  extension="docx"
                />
              </Stack>
            </Box>
          </TabPanel>
          <TabPanel value={tabConfig.dxf.value} keepMounted>
            <Box marginX={-2} mt={-2}>
              <PositionPlanDxf />
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
    )
  }, [setTab, tab])

  return <>{drawerContent}</>
}

export default Export
